import {
  AlertTriangle,
  Check,
  Copy,
  ChevronLeft,
  ChevronRight,
  ChevronsLeft,
  ChevronsRight,
  ImagePlus,
  LoaderCircle,
  MoreHorizontal,
  RefreshCw,
  Search,
  Star,
  X,
} from "lucide-react";
import {
  forwardRef,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
  type ButtonHTMLAttributes,
  type CSSProperties,
  type InputHTMLAttributes,
  type ReactNode,
  type SelectHTMLAttributes,
  type TextareaHTMLAttributes,
} from "react";
import { createPortal } from "react-dom";
import { adminApi, assetUrl } from "../lib/api";
import { cx } from "../lib/cx";

export type ButtonVariant = "primary" | "secondary" | "danger" | "ghost";

export function Button({
  children,
  variant = "primary",
  busy,
  className,
  ...props
}: ButtonHTMLAttributes<HTMLButtonElement> & {
  variant?: ButtonVariant;
  busy?: boolean;
}) {
  return (
    <button
      className={cx("button", `button--${variant}`, className)}
      disabled={busy || props.disabled}
      {...props}
    >
      {busy ? <LoaderCircle className="spin" size={17} /> : null}
      {children}
    </button>
  );
}

export function IconButton({
  label,
  children,
  className,
  ...props
}: ButtonHTMLAttributes<HTMLButtonElement> & {
  label: string;
  children: ReactNode;
}) {
  return (
    <button
      className={cx("icon-button", className)}
      type="button"
      aria-label={label}
      title={label}
      {...props}
    >
      {children}
    </button>
  );
}

export function CopyValue({ label, value, className }: { label: string; value?: string | null; className?: string }) {
  const [copied, setCopied] = useState(false);
  const copy = async () => {
    if (!value) return;
    try {
      await navigator.clipboard.writeText(value);
      setCopied(true);
      window.setTimeout(() => setCopied(false), 1400);
    } catch {
      setCopied(false);
    }
  };
  return (
    <div className={cx("copy-value", className)}>
      <span><small>{label}</small><bdi dir="ltr">{value || "ثبت نشده"}</bdi></span>
      <button type="button" disabled={!value} onClick={copy} aria-label={`کپی ${label}`} title={`کپی ${label}`}>{copied ? <Check size={14} /> : <Copy size={14} />}</button>
    </div>
  );
}

type FieldMeta = {
  label?: string;
  hint?: string;
  error?: string;
};

export const Input = forwardRef<HTMLInputElement, InputHTMLAttributes<HTMLInputElement> & FieldMeta>(
  function Input({ label, hint, error, className, ...props }, ref) {
    return (
      <label className={cx("field", className)}>
        {label ? <span className="field__label">{label}</span> : null}
        <input ref={ref} className={cx("input", error && "is-invalid")} {...props} />
        {error ? <small className="field__error">{error}</small> : hint ? <small className="field__hint">{hint}</small> : null}
      </label>
    );
  },
);

export const Textarea = forwardRef<HTMLTextAreaElement, TextareaHTMLAttributes<HTMLTextAreaElement> & FieldMeta>(
  function Textarea({ label, hint, error, className, ...props }, ref) {
    return (
      <label className={cx("field", className)}>
        {label ? <span className="field__label">{label}</span> : null}
        <textarea ref={ref} className={cx("textarea", error && "is-invalid")} {...props} />
        {error ? <small className="field__error">{error}</small> : hint ? <small className="field__hint">{hint}</small> : null}
      </label>
    );
  },
);

export const Select = forwardRef<
  HTMLSelectElement,
  SelectHTMLAttributes<HTMLSelectElement> & FieldMeta & { children: ReactNode }
>(function Select({ label, hint, error, children, className, ...props }, ref) {
  return (
    <label className={cx("field", className)}>
      {label ? <span className="field__label">{label}</span> : null}
      <select ref={ref} className={cx("select", error && "is-invalid")} {...props}>
        {children}
      </select>
      {error ? <small className="field__error">{error}</small> : hint ? <small className="field__hint">{hint}</small> : null}
    </label>
  );
});

export function Switch({
  label,
  checked,
  onChange,
  hint,
  disabled,
}: {
  label: string;
  checked: boolean;
  onChange: (value: boolean) => void;
  hint?: string;
  disabled?: boolean;
}) {
  return (
    <div className={cx("switch-field", checked && "is-checked", disabled && "is-disabled")}>
      <div className="switch-field__copy">
        <span className="switch-field__label">{label}</span>
        {hint ? <small>{hint}</small> : null}
      </div>
      <button
        type="button"
        className={cx("switch", checked && "is-on")}
        role="switch"
        aria-label={label}
        aria-checked={checked}
        disabled={disabled}
        onClick={() => onChange(!checked)}
      >
        <span />
      </button>
    </div>
  );
}

export function Card({
  children,
  title,
  description,
  action,
  className,
}: {
  children: ReactNode;
  title?: string;
  description?: string;
  action?: ReactNode;
  className?: string;
}) {
  return (
    <section className={cx("card", className)}>
      {title || action ? (
        <header className="card__header">
          <div>
            {title ? <h2>{title}</h2> : null}
            {description ? <p className="card__description">{description}</p> : null}
          </div>
          {action}
        </header>
      ) : null}
      <div className="card__body">{children}</div>
    </section>
  );
}

export function PageHeader({
  title,
  description,
  actions,
  eyebrow = "پنل مدیریت Lia Perfume",
}: {
  title: string;
  description?: string;
  actions?: ReactNode;
  eyebrow?: string;
}) {
  return (
    <header className="page-header">
      <div>
        <p className="eyebrow">{eyebrow}</p>
        <h1>{title}</h1>
        {description ? <p>{description}</p> : null}
      </div>
      {actions ? <div className="page-header__actions">{actions}</div> : null}
    </header>
  );
}

export function Badge({
  value,
  tone,
}: {
  value: string;
  tone?: "success" | "warning" | "danger" | "info" | "neutral";
}) {
  const derived = tone || (
    ["Published", "Paid", "Delivered", "Approved", "Active", "منتشرشده", "فعال", "پرداخت‌شده", "تحویل‌شده", "تأییدشده"].includes(value)
      ? "success"
      : ["Rejected", "Cancelled", "Archived", "ردشده", "لغوشده", "بایگانی"].includes(value)
        ? "danger"
        : ["Pending", "PendingPayment", "New", "در انتظار", "در انتظار پرداخت", "جدید"].includes(value)
          ? "warning"
          : "info"
  );

  return <span className={cx("badge", `badge--${derived}`)}>{value}</span>;
}

export function SearchBox({
  value,
  onChange,
  placeholder = "جست‌وجو...",
}: {
  value: string;
  onChange: (value: string) => void;
  placeholder?: string;
}) {
  return (
    <div className="search-box">
      <Search size={18} />
      <input value={value} onChange={(event) => onChange(event.target.value)} placeholder={placeholder} />
    </div>
  );
}

export function EmptyState({
  title = "موردی پیدا نشد",
  description = "فیلترها را تغییر دهید یا مورد جدیدی ثبت کنید.",
}: {
  title?: string;
  description?: string;
}) {
  return (
    <div className="empty-state">
      <div>
        <div className="empty-state__icon"><Search /></div>
        <h3>{title}</h3>
        <p>{description}</p>
      </div>
    </div>
  );
}

export function Loading({ label = "در حال دریافت اطلاعات..." }: { label?: string }) {
  return (
    <div className="loading">
      <LoaderCircle className="spin" />
      <span>{label}</span>
    </div>
  );
}

export function ErrorState({ error, retry }: { error: unknown; retry?: () => void }) {
  return (
    <div className="error-state">
      <AlertTriangle />
      <div>
        <strong>امکان انجام عملیات وجود نداشت</strong>
        <p>{error instanceof Error ? error.message : "خطای پیش‌بینی‌نشده‌ای رخ داد."}</p>
      </div>
      {retry ? <Button variant="secondary" onClick={retry}>تلاش دوباره</Button> : null}
    </div>
  );
}

export function FormErrorSummary({
  errors,
  title = "لطفاً خطاهای فرم را اصلاح کنید.",
}: {
  errors: Array<string | undefined | null>;
  title?: string;
}) {
  const unique = Array.from(new Set(errors.filter((value): value is string => Boolean(value))));
  if (!unique.length) return null;

  return (
    <div className="form-error-summary" role="alert">
      <strong>{title}</strong>
      <ul>{unique.slice(0, 8).map((item) => <li key={item}>{item}</li>)}</ul>
    </div>
  );
}

export function Pagination({
  page,
  totalPages,
  totalCount,
  pageSize,
  onChange,
  onPageSizeChange,
  pageSizeOptions = [5, 10, 20, 50, 100],
}: {
  page: number;
  totalPages: number;
  totalCount?: number;
  pageSize?: number;
  onChange: (page: number) => void;
  onPageSizeChange?: (size: number) => void;
  pageSizeOptions?: number[];
}) {
  const safeTotal = Math.max(1, totalPages);
  const pages = Array.from({ length: safeTotal }, (_, index) => index + 1).filter(
    (value) => value === 1 || value === safeTotal || Math.abs(value - page) <= 2,
  );
  const compact = pages.reduce<Array<number | "dots">>((acc, value, index) => {
    if (index && value - (pages[index - 1] ?? value) > 1) acc.push("dots");
    acc.push(value);
    return acc;
  }, []);

  if (totalPages <= 1 && !onPageSizeChange) return null;

  return (
    <nav className="pagination" aria-label="صفحه‌بندی">
      <div className="pagination__summary">
        {typeof totalCount === "number" ? <span>{totalCount.toLocaleString("fa-IR")} رکورد</span> : null}
        {pageSize && onPageSizeChange ? (
          <label>
            نمایش
            <select value={pageSize} onChange={(event) => onPageSizeChange(Number(event.target.value))}>
              {pageSizeOptions.map((size) => <option key={size} value={size}>{size.toLocaleString("fa-IR")}</option>)}
            </select>
            در هر صفحه
          </label>
        ) : null}
      </div>
      {totalPages > 1 ? (
        <div className="pagination__buttons">
          <button type="button" title="صفحه اول" disabled={page <= 1} onClick={() => onChange(1)}><ChevronsRight size={16} /></button>
          <button type="button" title="صفحه قبل" disabled={page <= 1} onClick={() => onChange(page - 1)}><ChevronRight size={16} /></button>
          {compact.map((item, index) => item === "dots" ? (
            <span className="pagination__dots" key={`d-${index}`}>…</span>
          ) : (
            <button type="button" className={item === page ? "is-current" : ""} key={item} onClick={() => onChange(item)}>
              {item.toLocaleString("fa-IR")}
            </button>
          ))}
          <button type="button" title="صفحه بعد" disabled={page >= totalPages} onClick={() => onChange(page + 1)}><ChevronLeft size={16} /></button>
          <button type="button" title="صفحه آخر" disabled={page >= totalPages} onClick={() => onChange(totalPages)}><ChevronsLeft size={16} /></button>
        </div>
      ) : null}
    </nav>
  );
}


export function DataTableShell({
  children,
  pagination,
  className,
}: {
  children: ReactNode;
  pagination?: ReactNode;
  className?: string;
}) {
  return (
    <div className={cx("data-table-block", className)}>
      <div className="table-card">
        <div className="table-scroll">{children}</div>
      </div>
      {pagination ? <div className="data-table-footer">{pagination}</div> : null}
    </div>
  );
}

export type SummaryItem = {
  label: string;
  value: ReactNode;
  hint?: ReactNode;
  icon?: ReactNode;
  tone?: "teal" | "gold" | "green" | "rose" | "neutral";
};

export function SummaryGrid({ items, compact = false }: { items: SummaryItem[]; compact?: boolean }) {
  return (
    <section className={cx("summary-grid", compact && "summary-grid--compact")} aria-label="خلاصه وضعیت">
      {items.map((item) => (
        <article className={cx("summary-metric", `summary-metric--${item.tone || "neutral"}`)} key={item.label}>
          {item.icon ? <span className="summary-metric__icon">{item.icon}</span> : null}
          <div>
            <span>{item.label}</span>
            <strong>{item.value}</strong>
            {item.hint ? <small>{item.hint}</small> : null}
          </div>
        </article>
      ))}
    </section>
  );
}

export function ActionMenu({ children, label = "عملیات" }: { children: ReactNode; label?: string }) {
  const [open, setOpen] = useState(false);
  const [position, setPosition] = useState({ top: 0, left: 0 });
  const triggerRef = useRef<HTMLButtonElement | null>(null);
  const menuRef = useRef<HTMLDivElement | null>(null);

  const place = () => {
    const trigger = triggerRef.current;
    if (!trigger) return;
    const rect = trigger.getBoundingClientRect();
    const width = 190;
    const left = Math.max(8, Math.min(rect.left, window.innerWidth - width - 8));
    setPosition({ top: rect.bottom + 7, left });
  };

  useLayoutEffect(() => {
    if (!open) return undefined;
    place();
    const frame = window.requestAnimationFrame(() => {
      const trigger = triggerRef.current;
      const menu = menuRef.current;
      if (!trigger || !menu) return;
      const triggerRect = trigger.getBoundingClientRect();
      const menuRect = menu.getBoundingClientRect();
      if (menuRect.bottom > window.innerHeight - 8) {
        setPosition((current) => ({ ...current, top: Math.max(8, triggerRect.top - menuRect.height - 7) }));
      }
    });
    return () => window.cancelAnimationFrame(frame);
  }, [open]);

  useEffect(() => {
    if (!open) return undefined;
    const closeOutside = (event: PointerEvent) => {
      const target = event.target as Node | null;
      if (triggerRef.current?.contains(target) || menuRef.current?.contains(target)) return;
      setOpen(false);
    };
    const closeKey = (event: KeyboardEvent) => { if (event.key === "Escape") setOpen(false); };
    const closeOnViewportChange = () => setOpen(false);
    document.addEventListener("pointerdown", closeOutside);
    document.addEventListener("keydown", closeKey);
    window.addEventListener("resize", closeOnViewportChange);
    window.addEventListener("scroll", closeOnViewportChange, true);
    return () => {
      document.removeEventListener("pointerdown", closeOutside);
      document.removeEventListener("keydown", closeKey);
      window.removeEventListener("resize", closeOnViewportChange);
      window.removeEventListener("scroll", closeOnViewportChange, true);
    };
  }, [open]);

  return (
    <>
      <div className="action-menu">
        <button
          ref={triggerRef}
          type="button"
          className="action-menu__trigger"
          aria-label={label}
          title={label}
          aria-haspopup="menu"
          aria-expanded={open}
          onClick={() => setOpen((value) => !value)}
        ><MoreHorizontal size={18} /></button>
      </div>
      {open ? createPortal(
        <div
          ref={menuRef}
          className="action-menu__content"
          role="menu"
          style={{ top: position.top, left: position.left }}
          onClick={(event) => {
            if ((event.target as HTMLElement).closest("button,a")) setOpen(false);
          }}
        >{children}</div>,
        document.body,
      ) : null}
    </>
  );
}

export function ActionMenuItem({
  children,
  danger = false,
  ...props
}: ButtonHTMLAttributes<HTMLButtonElement> & { children: ReactNode; danger?: boolean }) {
  return <button type="button" className={cx("action-menu__item", danger && "is-danger")} {...props}>{children}</button>;
}

export function Drawer({
  open,
  title,
  description,
  children,
  onClose,
  wide = false,
}: {
  open: boolean;
  title: string;
  description?: string;
  children: ReactNode;
  onClose: () => void;
  wide?: boolean;
}) {
  useEffect(() => {
    if (!open) return undefined;
    const onKeyDown = (event: KeyboardEvent) => { if (event.key === "Escape") onClose(); };
    document.addEventListener("keydown", onKeyDown);
    const previous = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.removeEventListener("keydown", onKeyDown);
      document.body.style.overflow = previous;
    };
  }, [open, onClose]);

  if (!open) return null;
  return (
    <div className="drawer-backdrop" role="presentation" onMouseDown={(event) => event.target === event.currentTarget && onClose()}>
      <aside className={cx("drawer", wide && "drawer--wide")} role="dialog" aria-modal="true" aria-label={title}>
        <header className="drawer__header">
          <div><h2>{title}</h2>{description ? <p>{description}</p> : null}</div>
          <IconButton label="بستن" onClick={onClose}><X /></IconButton>
        </header>
        <div className="drawer__body">{children}</div>
      </aside>
    </div>
  );
}

export function RatingStars({ value, size = 16 }: { value: number; size?: number }) {
  const safe = Math.max(0, Math.min(5, value));
  return (
    <span className="rating-stars" aria-label={`${safe.toLocaleString("fa-IR")} از ۵ ستاره`}>
      {Array.from({ length: 5 }, (_, index) => (
        <Star key={index} size={size} fill={index < safe ? "currentColor" : "none"} />
      ))}
    </span>
  );
}

export function FormSection({
  title,
  description,
  children,
  action,
}: {
  title: string;
  description?: string;
  children: ReactNode;
  action?: ReactNode;
}) {
  return (
    <section className="form-section">
      <header className="form-section__header">
        <div><h3>{title}</h3>{description ? <p>{description}</p> : null}</div>
        {action}
      </header>
      <div className="form-section__body">{children}</div>
    </section>
  );
}

export function Modal({
  open,
  title,
  children,
  onClose,
  wide = false,
}: {
  open: boolean;
  title: string;
  children: ReactNode;
  onClose: () => void;
  wide?: boolean;
}) {
  useEffect(() => {
    if (!open) return undefined;
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") onClose();
    };
    document.addEventListener("keydown", onKeyDown);
    const previous = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.removeEventListener("keydown", onKeyDown);
      document.body.style.overflow = previous;
    };
  }, [open, onClose]);

  if (!open) return null;

  return (
    <div
      className="modal-backdrop"
      role="presentation"
      onMouseDown={(event) => {
        if (event.target === event.currentTarget) onClose();
      }}
    >
      <section className={cx("modal", wide && "modal--wide")} role="dialog" aria-modal="true" aria-labelledby="modal-title">
        <header>
          <h2 id="modal-title">{title}</h2>
          <IconButton label="بستن" onClick={onClose}><X /></IconButton>
        </header>
        <div className="modal__body">{children}</div>
      </section>
    </div>
  );
}

export function Confirm({
  open,
  title,
  description,
  busy,
  confirmLabel = "تأیید",
  danger = true,
  onConfirm,
  onClose,
}: {
  open: boolean;
  title: string;
  description: string;
  busy?: boolean;
  confirmLabel?: string;
  danger?: boolean;
  onConfirm: () => void;
  onClose: () => void;
}) {
  return (
    <Modal open={open} title={title} onClose={onClose}>
      <div className="confirm">
        <AlertTriangle size={42} />
        <p>{description}</p>
        <div className="form-actions">
          <Button type="button" variant="ghost" onClick={onClose}>انصراف</Button>
          <Button type="button" variant={danger ? "danger" : "primary"} busy={busy} onClick={onConfirm}>{confirmLabel}</Button>
        </div>
      </div>
    </Modal>
  );
}

export function ImageUploader({
  value,
  onChange,
  label = "تصویر",
  hint,
}: {
  value?: string | null;
  onChange: (value: string) => void;
  label?: string;
  hint?: string;
}) {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const inputRef = useRef<HTMLInputElement | null>(null);

  const upload = async (file?: File) => {
    if (!file) return;
    setBusy(true);
    setError("");
    try {
      const result = await adminApi.uploads.image(file);
      onChange(result.url);
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : "آپلود تصویر ناموفق بود.");
    } finally {
      setBusy(false);
      if (inputRef.current) inputRef.current.value = "";
    }
  };

  return (
    <div className={cx("image-uploader", value && "has-image")}>
      <span className="field__label">{label}</span>
      {hint ? <small className="field__hint mb-2">{hint}</small> : null}
      <div className="image-uploader__box">
        {value ? (
          <>
            <img src={assetUrl(value)} alt="پیش‌نمایش" />
            <label className="image-uploader__replace" title="جایگزینی تصویر">
              {busy ? <LoaderCircle className="spin" size={18} /> : <RefreshCw size={18} />}
              <span className="sr-only">جایگزینی تصویر</span>
              <input
                ref={inputRef}
                type="file"
                accept="image/jpeg,image/png,image/webp"
                disabled={busy}
                onChange={(event) => void upload(event.target.files?.[0])}
              />
            </label>
          </>
        ) : (
          <div className="image-uploader__empty">
            <span className="image-uploader__empty-icon"><ImagePlus size={30} /></span>
            <div>
              <strong>هنوز تصویری انتخاب نشده است</strong>
              <small>یک فایل JPG، PNG یا WebP انتخاب کنید.</small>
            </div>
            <label className="button button--secondary">
              {busy ? <LoaderCircle className="spin" size={17} /> : <ImagePlus size={17} />}
              انتخاب و آپلود تصویر
              <input
                ref={inputRef}
                type="file"
                accept="image/jpeg,image/png,image/webp"
                disabled={busy}
                onChange={(event) => void upload(event.target.files?.[0])}
              />
            </label>
          </div>
        )}
      </div>
      {error ? <small className="field__error">{error}</small> : null}
    </div>
  );
}

export function Notice({
  children,
  tone = "info",
}: {
  children: ReactNode;
  tone?: "info" | "warning" | "success";
}) {
  return (
    <div className={cx("notice", `notice--${tone}`)}>
      {tone === "success" ? <Check /> : <AlertTriangle />}
      <div>{children}</div>
    </div>
  );
}

export { RichTextEditor } from "./RichTextEditor";
